<template lang="pug">
    div(class="cursor-pointer shadow-sm rounded h-full overflow-hidden bg-white relative pb-1 lg:mb-5 lg:shadow-none lg:overflow-visible lg:pb-0")
        nuxt-link(
            :to="linkBuilder({ name: 'product', params:{ productLine: listing.productLineSlug}})"
            class="no-underline hover:no-underline"
        )
            div(@click="$emit('productClicked')")
                div(class="relative m-auto")
                    div(class="image-background absolute w-full h-full")
                    img(
                        v-if="listing.image"
                        :key="listing.image.key"
                        v-bind="imgAttrs"
                        width="341"
                        height="256"
                        :alt="listing.image.title || ''"
                        class="w-full"
                        style="max-width: initial;"
                    )
                    img(
                        v-else
                        src="@/assets/coming-soon.jpg"
                        class="w-full"
                        style="max-width: initial;"
                    )
                    div(class="absolute top-0 right-0")
                        div(class="flex space-x-1 h-[26px]")
                            div(
                                v-if="listing.badgeText"
                                class="or-product-listing-special-tag bg-danger-dark text-light text-xs font-bold uppercase text-center px-2 flex items-center"
                            ) {{ listing.badgeText }}
                            div(
                                v-else-if="listing.isPreOrder"
                                class="or-product-listing-pre-order-tag bg-info text-light text-xs font-bold uppercase text-center px-2 flex items-center"
                            ) PRE-ORDER
                            div(
                                v-else-if="listing.isNew"
                                class="or-product-listing-new-tag bg-info text-light text-xs font-bold uppercase text-center px-2 flex items-center"
                            ) NEW ITEM
                            MadeByRealtruck(
                                v-if="listing.brand && $sitewideData.config.madeByRealTruckEnabled"
                                :brand="listing.brand"
                            )
                div(
                    data-testid="productLineName"
                    v-html="listing.productLineName"
                    class="pt-3 leading-5 text-black font-bold lg:p-0 lg:mt-3"
                )
                div(
                    v-if="showPrice"
                    class="pt-1 lg:pt-0"
                )
                    span(
                        class="text-gray-darkest text-xs uppercase"
                    ) From:&nbsp;&nbsp;
                    span(
                        :data-discount="listing.hasDiscount"
                        class="text-gray-darkest text-lg font-bold mr-2 md:text-2xl"
                        :class="[{ 'or-product-listing-sale-price text-danger' : listing.hasDiscount }]"
                    ) {{ formatCents(listing.salePrice) }}
                    span(
                        v-if="listing.hasDiscount"
                        class="line-through inline-block text-sm text-gray-dark"
                    ) {{ formatCents(listing.originalPrice) }}

                div(
                    v-if="showReviews || showSameDayShipping"
                    class="flex flex-wrap items-center relative"
                )
                    Stars(
                        v-if="showReviews"
                        :averageRating="listing.averageRating"
                        :totalReviews="listing.totalReviews"
                        class="mr-2 py-1"
                    )
                    template(v-if="showSameDayShipping && ($sitewideData.config.sameDayShippingEnabled && listing.sameDayShipping)")
                        Tooltip(
                            mode="hover"
                            text="This product may ship the same day, depending on your vehicle."
                            :hoverDelay="750"
                        )
                            SameDayShippingLogo(class="py-1 w-24")
                p(v-if="showShopNow" class="pt-1 text-action font-bold") Shop Now

        script(v-if="isSchemaOn" type="application/ld+json" v-html="getSchema(listing)")
</template>

<script>
import { linkBuilders } from '@/helpers/mixins'
import { formatCents } from '@/helpers/filters'
import Stars from '@/components/stars'
import SameDayShippingLogo from '@/components/shipping/sameDayLogo'
import Tooltip from '@/components/tooltip'
import MadeByRealtruck from '@/components/madeByRealtruck'

export default {
    name: 'ProductListing',
    components: {
        Stars,
        SameDayShippingLogo,
        Tooltip,
        MadeByRealtruck,
    },
    mixins: [linkBuilders],
    props: {
        listing: {
            type: Object,
            default: () => {},
        },
        isLazy: {
            type: Boolean,
            default: true,
        },
        isSchemaOn: {
            type: Boolean,
            default: true,
        },
        showShopNow: {
            type: Boolean,
            default: false,
        },
        showPrice: {
            type: Boolean,
            default: true,
        },
        showReviews: {
            type: Boolean,
            default: true,
        },
        showSameDayShipping: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        imgAttrs() {
            const imgSrc = this.$getImageUrl(
                this.listing.image.key,
                this.listing.image.filename,
                341,
                256,
                70,
                'fff'
            )
            return {
                src: imgSrc,
                ...(this.isLazy && {
                    loading: 'lazy',
                }),
            }
        },
    },
    methods: {
        formatCents,
        getSchema(obj) {
            const schema = {
                '@context': 'http://schema.org/',
                '@type': 'Product',
                name: obj.productLineName,
                url:
                    'https://' +
                    this.$sitewideData.domain +
                    this.$router.resolve(
                        this.linkBuilder({
                            name: 'product',
                            params: { productLine: obj.productLineSlug },
                        })
                    ).href,
                image: this.$getImageUrl(obj.image.key, obj.image.filename),
                brand: {
                    '@type': 'Brand',
                    name: obj.brand,
                },
            }

            if (obj.totalReviews > 0) {
                const agg = {}
                agg['@type'] = 'AggregateRating'
                agg.ratingValue = obj.averageRating
                agg.reviewCount = obj.totalReviews
                schema.aggregateRating = agg
            }

            if (!obj.hasMAPViolation) {
                const offer = {}
                offer['@type'] = 'Offer'
                offer.price = obj.salePrice / 100
                offer.priceCurrency = 'USD'
                schema.offers = offer
            }

            return JSON.stringify(schema)
        },
    },
}
</script>

<style lang="stylus" scoped>
.image-background
    background: radial-gradient(50% 50% at center, rgba(55, 62, 62, 0.00) 50%, rgba(55, 62, 62, 0.01) 74%, rgba(55, 62, 62, 0.05) 100%)
</style>
